/**
 * Login Microsoft em duas etapas (specs/002-microsoft-auth-idtoken).
 *
 * Substitui o antigo POST /post_login { email, auth_token } — que mandava um
 * segredo público e não provava nada — por:
 *
 *   1. POST /auth/microsoft           Authorization: Bearer <idToken do MSAL>
 *        → { access_token, email }    (idToken validado no servidor: assinatura,
 *                                       exp, aud, iss, allowlist de domínio)
 *   2. POST /auth/microsoft/profile   Authorization: Bearer <access_token IRIS>
 *        body { email }               (opcional; tem que bater com a sessão)
 *        → { role, is_manager, domains_access, email, assistants, popup_info, terms_info }
 *
 * O retorno tem o MESMO shape que `loginData` tinha antes
 * (`{ access_token, ...perfil }`), para o bloco `set*` das páginas de login não
 * mudar. `loginData.access` continua indefinido (nunca foi devolvido).
 *
 * Em 401/403 de qualquer das duas etapas lança `MicrosoftAuthError` com
 * `status` e `code` — as páginas caem no fluxo "sem acesso" já existente.
 */

export class MicrosoftAuthError extends Error {
  constructor(status, code, message) {
    super(message || `Microsoft auth failed (${status} ${code || ''})`.trim());
    this.name = 'MicrosoftAuthError';
    this.status = status;
    this.code = code || null;
  }
}

const API_URL = () => process.env.REACT_APP_API_URL;

async function readErrorCode(response) {
  try {
    const body = await response.json();
    return body && body.error ? body.error : null;
  } catch {
    return null;
  }
}

/**
 * @param {import('@azure/msal-browser').IPublicClientApplication} instance
 * @param {import('@azure/msal-browser').AccountInfo} account
 * @returns {Promise<object>} objeto com o shape de `loginData`
 * @throws {MicrosoftAuthError} em 401/403 (ou outro status não-OK) do backend
 */
export async function authenticateWithMicrosoft(instance, account) {
  // Mesma chamada que já é feita para o Graph; aqui usamos o idToken
  // (aud = clientId da IRIS), não o accessToken (aud = Graph).
  const tokenResponse = await instance.acquireTokenSilent({ scopes: ['User.Read'], account });
  const idToken = tokenResponse && tokenResponse.idToken;
  if (!idToken) {
    throw new MicrosoftAuthError(0, 'missing_id_token', 'MSAL não devolveu idToken');
  }

  const authResponse = await fetch(`${API_URL()}/auth/microsoft`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${idToken}` },
  });
  if (!authResponse.ok) {
    throw new MicrosoftAuthError(authResponse.status, await readErrorCode(authResponse));
  }
  const { access_token, email } = await authResponse.json();

  const profileResponse = await fetch(`${API_URL()}/auth/microsoft/profile`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });
  if (!profileResponse.ok) {
    throw new MicrosoftAuthError(profileResponse.status, await readErrorCode(profileResponse));
  }
  const profile = await profileResponse.json();

  return { access_token, email, ...profile };
}

/** true para recusas de autenticação/autorização que devem cair no fluxo "sem acesso". */
export function isUnauthorizedError(err) {
  return err instanceof MicrosoftAuthError && (err.status === 401 || err.status === 403);
}
